import { type Graph, type GraphEdge, graphFromJson, nodeById } from './graph';

export type ProblemType =
  | 'shortestPathPlanar'
  | 'shortestPath'
  | 'mst'
  | 'maxFlow'
  | 'minCut'
  | 'matching'
  | 'interdiction'
  | 'tsp';

export type NodeStyle = 'building' | 'crossing';

export interface StarThresholds {
  readonly threeStar: number;
  readonly twoStar: number;
  readonly oneStar: number;
}

export interface HintData {
  readonly tier: number;
  readonly text?: string;
  readonly highlightEdges?: readonly string[];
  readonly highlightNodes?: readonly string[];
}

export interface BackgroundBuilding {
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly scale: number;
}

export interface BackgroundPatch {
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly scale: number;
  readonly rotation: number;
}

export interface CitySheet {
  readonly cityId: string;
  readonly region: { left: number; top: number; width: number; height: number };
}

export interface LevelPack {
  readonly index: number;
  readonly name: string;
  readonly rule: string;
}

export interface Level {
  readonly id: string;
  readonly worldIndex: number;
  readonly levelIndex: number;
  readonly title: string;
  readonly objective: string;
  readonly type: ProblemType;
  readonly graph: Graph;
  readonly optimalValue: number;
  readonly optimalSolutionEdgeIds: readonly string[];
  readonly thresholds: StarThresholds;
  readonly hints: readonly HintData[];
  readonly budget: number;
  readonly mechanics: readonly string[];
  readonly nodeStyle: NodeStyle;
  readonly backgroundBuildings: readonly BackgroundBuilding[];
  readonly backgroundPatches: readonly BackgroundPatch[];
  readonly useDistanceWeights: boolean;
  readonly chromeOverrides?: Record<string, unknown>;
  readonly citySheet?: CitySheet;
  readonly pack?: LevelPack;
}

export function computeDistanceWeights(graph: Graph): Graph {
  const edges: GraphEdge[] = graph.edges.map((edge) => {
    const source = nodeById(graph, edge.sourceId);
    const target = nodeById(graph, edge.targetId);
    if (!source || !target) return edge;

    const dx = source.position.x - target.position.x;
    const dy = source.position.y - target.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const weight = Math.min(99, Math.max(1, Math.round(distance * 10)));

    return { ...edge, weight };
  });

  return { nodes: graph.nodes, edges, directed: graph.directed };
}

export function levelFromJson(json: Record<string, any>): Level {
  const useDistanceWeights = (json.useDistanceWeights as boolean) ?? false;
  const optimalSolutionEdgeIds = (json.optimalSolutionEdgeIds as string[]) ?? [];

  let graph = graphFromJson(json.graph);
  let optimalValue: number;
  let thresholds: StarThresholds;

  if (useDistanceWeights) {
    graph = computeDistanceWeights(graph);
    optimalValue = optimalSolutionEdgeIds.reduce((sum, edgeId) => {
      const edge = graph.edges.find((e) => e.id === edgeId);
      return sum + (edge?.weight ?? 0);
    }, 0);
    thresholds = {
      threeStar: optimalValue,
      twoStar: Math.round(optimalValue * 1.4),
      oneStar: Math.round(optimalValue * 2.0),
    };
  } else {
    optimalValue = Number(json.optimalValue);
    thresholds = json.thresholds as StarThresholds;
  }

  return {
    id: json.id as string,
    worldIndex: json.worldIndex as number,
    levelIndex: json.levelIndex as number,
    title: json.title as string,
    objective: json.objective as string,
    type: json.type as ProblemType,
    graph,
    optimalValue,
    optimalSolutionEdgeIds,
    thresholds,
    hints: (json.hints as HintData[]) ?? [],
    budget: (json.budget as number) ?? 2,
    mechanics: (json.mechanics as string[]) ?? [],
    nodeStyle: json.nodeStyle === 'crossing' ? 'crossing' : 'building',
    backgroundBuildings: ((json.backgroundBuildings as any[]) ?? []).map((b) => ({
      type: b.type as string,
      position: { x: Number(b.x), y: Number(b.y) },
      scale: b.scale === undefined ? 1 : Number(b.scale),
    })),
    backgroundPatches: ((json.backgroundPatches as any[]) ?? []).map((p) => ({
      type: p.type as string,
      position: { x: Number(p.x), y: Number(p.y) },
      scale: p.scale === undefined ? 1 : Number(p.scale),
      rotation: p.rotation === undefined ? 0 : Number(p.rotation),
    })),
    useDistanceWeights,
    ...(json.chromeOverrides ? { chromeOverrides: json.chromeOverrides } : {}),
    ...(json.citySheet ? { citySheet: json.citySheet as CitySheet } : {}),
    ...(json.pack ? { pack: json.pack as LevelPack } : {}),
  };
}

export function isHigherBetter(type: ProblemType): boolean {
  return type === 'maxFlow' || type === 'matching' || type === 'interdiction';
}

export function calculateStars(
  score: number,
  thresholds: StarThresholds,
  type: ProblemType,
): number {
  if (isHigherBetter(type)) {
    if (score >= thresholds.threeStar) return 3;
    if (score >= thresholds.twoStar) return 2;
    if (score >= thresholds.oneStar) return 1;
    return 0;
  }
  if (score <= thresholds.threeStar) return 3;
  if (score <= thresholds.twoStar) return 2;
  if (score <= thresholds.oneStar) return 1;
  return 0;
}

export function oneWayEdgeIds(graph: Graph): Set<string> {
  return new Set(graph.edges.filter((e) => e.metadata?.constraint === 'oneWay').map((e) => e.id));
}
